import { matchPath } from 'react-router';
import { config } from 'ENV_CONFIG';

import { getAnalyticsJsonDataMap } from './analytics-json-data-map';
import { securedPath } from './analytics.constants';
import { isNotEmpty } from '~/utils';

const rejectTimeout = 60 * 1000;

export const bootstrapperDataStateChangeReady = new Promise((resolve, reject) => {
  let intervalId;

  const timeoutId = setTimeout(() => {
    reject(new Error('Can not dispatch date state change. Analytics were not readied.'));
    clearInterval(intervalId);
  }, rejectTimeout);

  intervalId = setInterval(() => {
    if (window.Bootstrapper && window.Bootstrapper.dataStateChangeReady === true) {
      resolve();
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    }
  }, 250);
}).catch(() => {});

export const dispatchAnalyticsEventWithAction = (action, additionalData = {}) => {
  const customEvent = new CustomEvent('dataStateChange', {
    detail: {
      action,
      ...additionalData,
    },
  });

  bootstrapperDataStateChangeReady.then(() => {
    document.body.dispatchEvent(customEvent);
  });
};

export const updateWindowDataLayer = (data) => {
  window.jlData = data;
};

export const getAppointmentEdits = () => {
  if (isNotEmpty(window.jlData)) {
    return window.jlData.page.appointments
      ? window.jlData.page.appointments.appointmentEdits
      : undefined;
  }
  return undefined;
};

export const buildErrorAnalyticsData = (path, shopId, errorMessage) => {
  const appointmentEdits = getAppointmentEdits();
  const analyticsData = getAnalyticsJsonDataMap(path);
  if (!analyticsData) return null;
  if (shopId) {
    analyticsData.page.appointments = {
      ...analyticsData.page.appointments,
      branchId: shopId,
    };
  }
  if (appointmentEdits !== undefined) {
    analyticsData.page.appointments = {
      ...analyticsData.page.appointments,
      appointmentEdits,
    };
  }
  analyticsData.page.error = errorMessage;
  return analyticsData;
};

export const isSecuredPath = (path) =>
  securedPath.some((secured) =>
    matchPath(path, {
      path: secured,
    }),
  );

export const buildPageLoadAnalyticsData = (path, shopId, appointmentsDetails, customerInfo) => {
  const appointmentEdits = getAppointmentEdits();
  const analyticsData = getAnalyticsJsonDataMap(path);
  if (!analyticsData) return null;
  if (appointmentsDetails) {
    analyticsData.page.appointments = appointmentsDetails;
  }
  if (shopId) {
    analyticsData.page.appointments = {
      ...analyticsData.page.appointments,
      branchId: shopId,
    };
  }
  if (appointmentEdits !== undefined) {
    analyticsData.page.appointments = {
      ...analyticsData.page.appointments,
      appointmentEdits,
    };
  }
  if (customerInfo) {
    analyticsData.page.customer = { customerInfo };
  }
  return analyticsData;
};

export const updateAppointmentsDetailsInWindowDataLayer = (appointmentDetails) => {
  if (window.jlData && window.jlData.page && window.jlData.page.appointments) {
    window.jlData.page.appointments = {
      ...window.jlData.page.appointments,
      ...appointmentDetails,
    };
  } else {
    window.jlData = {
      page: {
        appointments: appointmentDetails,
      },
    };
  }
};

export const updateDataLayerWithBookingId = (bookingId) => {
  if (window.jlData && window.jlData.page) {
    window.jlData.page.bookingReference = bookingId;
  } else {
    window.jlData = {
      page: {
        bookingReference: bookingId,
      },
    };
  }
};

export const updateAppointmentsDetailsWithAvailability = (availability) => {
  if (window.jlData && window.jlData.page && window.jlData.page.appointments) {
    window.jlData.page.appointments = {
      ...window.jlData.page.appointments,
      availability,
    };
  } else {
    window.jlData = {
      page: {
        appointments: {
          availability,
        },
      },
    };
  }
};

export const formatAppointmentEditsInWindowDataLayer = (appointmentEdits) => {
  const editedAppointments = window?.jlData?.page?.appointments?.appointmentEdits;

  if (!editedAppointments) {
    return appointmentEdits;
  }

  const editingItems = appointmentEdits.split(':');
  const editingSection = editingItems[0];
  const editingItem = editingItems[1];
  const regExp = new RegExp(`(${editingSection}.*?)(\\||$)`);
  const match = editedAppointments.match(regExp);

  if (match) {
    const matchedEdits = match[1];
    if (!editingItem || matchedEdits.includes(editingItem)) {
      return editedAppointments;
    }
    return editedAppointments.replace(matchedEdits, `${matchedEdits}:${editingItem}`);
  }
  return `${editedAppointments}|${appointmentEdits}`;
};

export const getAppointmentDetailsReferrer = (
  recommendedByAFriendQuestionAnswer,
  questionsForm,
) => {
  if (recommendedByAFriendQuestionAnswer) {
    return { referrer: 'Trial Recommended by friend' };
  }

  const section = questionsForm?.analyticsQuestionSection ?? {};
  const analyticsQuestion = config.howDidYouHearAboutOurServiceAnalyticsQuestions[0];

  const analyticsQuestionParentAnswer = section[analyticsQuestion.reduxFieldConfig.name];
  if (isNotEmpty(analyticsQuestionParentAnswer)) {
    const analyticsChildQuestionOptionId = Object.keys(
      analyticsQuestion.children[0].displayCondition.selectedOptions,
    )[0];
    const isChildQuestionDisplayed =
      analyticsQuestion.options.find((option) => option.id === analyticsChildQuestionOptionId)
        .name === analyticsQuestionParentAnswer[0];
    if (isChildQuestionDisplayed) {
      return { referrer: section[analyticsQuestion.children[0].reduxFieldConfig.name][0] };
    }
    return { referrer: analyticsQuestionParentAnswer[0] };
  }
  return {};
};

const appliedFiltersFormat = [
  { label: 'categories', group: 'category' },
  { label: 'brands', group: 'brand' },
];

export const formatAppliedFilters = (appliedFilters) => {
  if (!appliedFilters.length) return appliedFiltersFormat.map((el) => `${el.label}:none`).join('^');

  const groupedByType = appliedFilters.reduce((accum, filter) => {
    const next = accum;
    (next[filter.group] = next[filter.group] || []).push(filter);
    return next;
  }, {});

  return appliedFiltersFormat
    .map((format) => {
      const filters =
        groupedByType[format.group] && groupedByType[format.group].map((filter) => filter.name);

      return `${format.label}:${filters ? `${filters.join('|')}` : 'none'}`;
    })
    .join('^');
};

export const getPartnerGroupName = (selectedPartnerGroupName, partnersGroups) =>
  partnersGroups && partnersGroups[0].name !== selectedPartnerGroupName
    ? selectedPartnerGroupName
    : 'default';

export const analyticsUtils = {
  getPartnerGroupName,
};
